import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Package, ArrowLeft, Check, Search, Lock } from 'lucide-react';
import nurseryService from '../../services/nursery';
import { useStockSeat } from '../../hooks/useStockSeat';
import type {
  NurseryTreeType, NurseryStockItem, CreateStockItemPayload, RetailCategory, StockItemKind,
} from '../../types/nursery';
import { StockItemBarcodes } from '../../components/nursery/StockItemBarcodes';

// A plant is a cultivar from the registry at a tree type; merchandise has
// neither and is named (Rob, 2026-10-12). The server holds the same rule.
const makeStockItemSchema = (t: TFunction) => z.object({
  kind: z.enum(['plant', 'merchandise']),
  cultivar_id: z.number().optional(),
  rootstock_id: z.number().optional(),
  tree_type_id: z.number().optional(),
  name: z.string().max(200, t('validation.nameMax')).optional(),
  unit: z.string().max(20, t('validation.unitMax')).optional(),
  category_id: z.number().optional(),
  sku_code: z.string().optional(),
  barcode: z.string().max(30, t('validation.barcodeMax')).optional(),
  description: z.string().optional(),
  website_price: z.number({ message: t('validation.price') }).min(0, t('validation.priceMin')).optional(),
}).superRefine((d, ctx) => {
  if (d.kind === 'merchandise') {
    if (!d.name?.trim()) ctx.addIssue({ code: 'custom', path: ['name'], message: t('validation.name') });
    return;
  }
  if (!d.cultivar_id) ctx.addIssue({ code: 'custom', path: ['cultivar_id'], message: t('validation.cultivar') });
  if (!d.tree_type_id) ctx.addIssue({ code: 'custom', path: ['tree_type_id'], message: t('validation.treeType') });
});

type StockItemFormData = z.infer<ReturnType<typeof makeStockItemSchema>>;

// Empty number inputs/selects come back as '' — coerce to undefined so the
// optional() branch of the zod schema applies instead of failing on NaN.
const optionalNumber = (v: unknown) =>
  v === '' || v === null || v === undefined ? undefined : Number(v);

function StockItemForm() {
  const { t } = useTranslation('nursery', { keyPrefix: 'StockItemForm' });
  const stockItemSchema = useMemo(() => makeStockItemSchema(t), [t]);
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [searchParams] = useSearchParams();
  // New: chosen on the form (or ?kind=merchandise from the list). Edit: fixed.
  const [kind, setKind] = useState<StockItemKind>(
    searchParams.get('kind') === 'merchandise' ? 'merchandise' : 'plant',
  );
  const merch = kind === 'merchandise';
  const [categories, setCategories] = useState<RetailCategory[]>([]);
  // Stock item (SKU) catalogue management requires the nursery.admin tier.
  // All hooks must run before any early return (see CLAUDE.md hooks-first rule).
  const { hasAccess, loading: licenseLoading } = useStockSeat('admin');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState(true);
  // Offered for sale. Null until somebody ticks it: it then follows the tree
  // type, and a propagation tree type (cells, liners) starts not for sale.
  const [sellable, setSellable] = useState<boolean | null>(null);

  // Tree types
  const [treeTypes, setTreeTypes] = useState<NurseryTreeType[]>([]);

  // Cultivar search
  const [cultivarQuery, setCultivarQuery] = useState('');
  const [cultivarResults, setCultivarResults] = useState<any[]>([]);
  const [selectedCultivar, setSelectedCultivar] = useState<any | null>(null);

  // Rootstock search
  const [rootstockQuery, setRootstockQuery] = useState('');
  const [rootstockResults, setRootstockResults] = useState<any[]>([]);
  const [selectedRootstock, setSelectedRootstock] = useState<any | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<StockItemFormData>({
    resolver: zodResolver(stockItemSchema),
    defaultValues: { kind },
  });

  const chooseKind = (k: StockItemKind) => {
    setKind(k);
    setValue('kind', k);
  };

  // Load tree types
  useEffect(() => {
    nurseryService.getTreeTypes().then(res => {
      if (res.success) setTreeTypes(res.data || []);
    });
    nurseryService.getRetailCategories().then(res => {
      if (res.success) setCategories(res.data || []);
    }).catch(() => { /* an older server: no categories */ });
  }, []);

  // Cultivars this nursery is licensed to propagate (active nursery_propagation
  // licences) — the stock-item picker is restricted to these, NOT the global
  // registry. Loaded once, then filtered client-side as the user types.
  const [licensedCultivars, setLicensedCultivars] = useState<any[]>([]);
  const [licensedLoaded, setLicensedLoaded] = useState(false);

  useEffect(() => {
    nurseryService.getLicensedCultivars().then(res => {
      if (res.success) setLicensedCultivars(res.data || []);
      setLicensedLoaded(true);
    }).catch(() => setLicensedLoaded(true));
  }, []);

  // Existing item (edit mode). The joined cultivar/rootstock names are used to
  // seed the pickers so the user sees what is selected without re-searching.
  useEffect(() => {
    if (!isEdit) return;
    nurseryService.getStockItem(Number(id)).then(res => {
      if (res.success && res.data) {
        const s: NurseryStockItem = res.data;
        const price = s.website_price == null ? undefined : Number(s.website_price);
        const k: StockItemKind = s.kind === 'merchandise' ? 'merchandise' : 'plant';
        setKind(k);
        reset({
          kind: k,
          cultivar_id: s.cultivar_id ?? undefined,
          rootstock_id: s.rootstock_id ?? undefined,
          tree_type_id: s.tree_type_id ?? undefined,
          name: s.name ?? undefined,
          unit: s.unit ?? undefined,
          category_id: s.category_id ?? undefined,
          sku_code: s.sku_code ?? undefined,
          barcode: s.barcode ?? undefined,
          description: s.description ?? undefined,
          website_price: price != null && !Number.isNaN(price) ? price : undefined,
        });
        setActive(s.active);
        setSellable(s.sellable ?? true);
        if (s.cultivar_id) {
          setSelectedCultivar({
            id: s.cultivar_id,
            cultivar_name: s.cultivar_name,
            trade_name: s.cultivar_trade_name,
          });
        }
        if (s.rootstock_id) {
          setSelectedRootstock({ id: s.rootstock_id, cultivar_name: s.rootstock_name });
        }
      } else {
        setError(t('notFound'));
      }
      setLoading(false);
    }).catch(e => {
      setError(e instanceof Error ? e.message : t('loadFailed'));
      setLoading(false);
    });
  // t only words a load error; a language change must not reload and reset the form.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit, reset]);

  useEffect(() => {
    if (cultivarQuery.length < 1) { setCultivarResults([]); return; }
    const q = cultivarQuery.toLowerCase();
    setCultivarResults(
      licensedCultivars.filter(c =>
        c.cultivar_role !== 'rootstock' && (
        (c.cultivar_name || '').toLowerCase().includes(q) ||
        (c.trade_name || '').toLowerCase().includes(q)),
      ).slice(0, 10),
    );
  }, [cultivarQuery, licensedCultivars]);

  // Rootstock search — the same propagable list as the scion picker (open, or
  // held under a licence), narrowed to cultivars marked rootstock or dual on
  // the cultivar page. A PBR-protected rootstock this nursery holds no licence
  // for is not offered.
  useEffect(() => {
    if (rootstockQuery.length < 1) { setRootstockResults([]); return; }
    const q = rootstockQuery.toLowerCase();
    setRootstockResults(
      licensedCultivars.filter(c =>
        (c.cultivar_role === 'rootstock' || c.cultivar_role === 'dual') && (
        (c.cultivar_name || '').toLowerCase().includes(q) ||
        (c.trade_name || '').toLowerCase().includes(q)),
      ).slice(0, 10),
    );
  }, [rootstockQuery, licensedCultivars]);

  const handleSelectCultivar = (cultivar: any) => {
    setSelectedCultivar(cultivar);
    setValue('cultivar_id', cultivar.id);
    setCultivarQuery('');
    setCultivarResults([]);
  };

  const handleSelectRootstock = (rootstock: any) => {
    setSelectedRootstock(rootstock);
    setValue('rootstock_id', rootstock.id);
    setRootstockQuery('');
    setRootstockResults([]);
  };

  const onSubmit = async (data: StockItemFormData) => {
    setSubmitting(true);
    setError(null);
    try {
      const price = data.website_price != null && !Number.isNaN(data.website_price)
        ? data.website_price
        : null;

      const isMerch = data.kind === 'merchandise';
      if (isEdit) {
        // Send explicit nulls so a cleared rootstock / price / SKU is actually unset.
        const res = await nurseryService.updateStockItem(Number(id), {
          ...(isMerch
            ? { name: data.name?.trim() || '', unit: data.unit?.trim() || null }
            : {
              cultivar_id: data.cultivar_id ?? null,
              tree_type_id: data.tree_type_id ?? null,
              rootstock_id: data.rootstock_id ?? null,
            }),
          category_id: data.category_id ?? null,
          sku_code: data.sku_code?.trim() || null,
          barcode: data.barcode?.replace(/\s+/g, '') || null,
          description: data.description?.trim() || null,
          ...(isMerch ? {} : { website_price: price }),
          active,
          ...(sellable != null ? { sellable } : {}),
        });
        if (res.success) {
          navigate('/nursery/stock-items');
        } else {
          setError((res as any).error?.message || t('saveFailed'));
        }
        return;
      }

      const payload: CreateStockItemPayload = {
        kind: data.kind,
        ...(isMerch
          ? { name: data.name?.trim(), ...(data.unit?.trim() ? { unit: data.unit.trim() } : {}) }
          : {
            cultivar_id: data.cultivar_id,
            tree_type_id: data.tree_type_id,
            ...(data.rootstock_id ? { rootstock_id: data.rootstock_id } : {}),
          }),
        ...(data.category_id ? { category_id: data.category_id } : {}),
        ...(data.sku_code ? { sku_code: data.sku_code } : {}),
        ...(data.barcode?.trim() ? { barcode: data.barcode.replace(/\s+/g, '') } : {}),
        ...(data.description ? { description: data.description } : {}),
        ...(!isMerch && price != null ? { website_price: price } : {}),
        ...(sellable != null ? { sellable } : {}),
      };
      const res = await nurseryService.createStockItem(payload);
      if (res.success) {
        navigate('/nursery/stock-items');
      } else {
        setError((res as any).error?.message || t('createFailed'));
      }
    } catch (e: any) {
      setError(e?.message || t('unexpected'));
    } finally {
      setSubmitting(false);
    }
  };

  const chosenTreeType = treeTypes.find(tt => tt.id === watch('tree_type_id'));
  const sellableShown = sellable ?? chosenTreeType?.purpose !== 'propagation';

  if (licenseLoading || loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="h-8 bg-neutral-100 rounded animate-pulse w-64 mb-4" />
        <div className="h-40 bg-neutral-100 rounded animate-pulse" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
          <Lock className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-neutral-800 mb-1">{t('noAccess.title')}</h2>
          <p className="text-sm text-neutral-500 mb-4">
            <Trans t={t} i18nKey="noAccess.body" components={{ b: <span className="font-medium" /> }} />
          </p>
          <Button variant="outline" size="sm" onClick={() => navigate('/nursery/stock-items')}>
            <ArrowLeft className="h-4 w-4 mr-1" /> {t('noAccess.back')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={isEdit ? (merch ? t('titles.editMerch') : t('titles.editItem')) : (merch ? t('titles.newMerch') : t('titles.newItem'))}
        icon={<Package className="h-6 w-6 text-white" />}
      />
      <div className="max-w-2xl mx-auto px-4 py-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="bg-white border border-neutral-200 rounded-xl p-6 space-y-5">
            {/* What it is. Fixed once made: orders, prices and lots were made for it. */}
            {isEdit ? (
              <p className="text-xs font-medium text-neutral-500">
                {merch ? t('kind.merchandise') : t('kind.plant')}
              </p>
            ) : (
              <fieldset>
                <legend className="block text-sm font-medium text-neutral-700 mb-1">{t('kind.question')}</legend>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {([
                    ['plant', t('kind.aPlant'), t('kind.plantHint')],
                    ['merchandise', t('kind.merchandise'), t('kind.merchandiseHint')],
                  ] as const).map(([value, label, hint]) => (
                    <label
                      key={value}
                      className={`flex items-start gap-2 border rounded-lg px-3 py-2 cursor-pointer ${
                        kind === value ? 'border-primary-500 bg-primary-50' : 'border-neutral-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="stock-item-kind"
                        value={value}
                        checked={kind === value}
                        onChange={() => chooseKind(value)}
                        className="mt-1"
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-neutral-800">{label}</span>
                        <span className="block text-xs text-neutral-500">{hint}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>
            )}

            {merch && (
              <>
                <div>
                  <label htmlFor="stock-item-name" className="block text-sm font-medium text-neutral-700 mb-1">
                    {t('name')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="stock-item-name"
                    type="text"
                    {...register('name')}
                    placeholder={t('namePlaceholder')}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name.message}</p>}
                </div>
                <div>
                  <label htmlFor="stock-item-unit" className="block text-sm font-medium text-neutral-700 mb-1">{t('soldBy')}</label>
                  <input
                    id="stock-item-unit"
                    type="text"
                    {...register('unit')}
                    placeholder={t('unitPlaceholder')}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  {errors.unit
                    ? <p className="text-xs text-red-500 mt-1">{errors.unit.message}</p>
                    : <p className="text-xs text-neutral-500 mt-1">{t('unitHelp')}</p>}
                </div>
              </>
            )}

            {!merch && (<>
            {/* Cultivar picker — restricted to licensed cultivars */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                {t('cultivar')} <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-neutral-500 mb-1">
                <Trans t={t} i18nKey="cultivarHelp" components={{ link: <Link to="/cultivar/create" className="text-primary-600 underline" /> }} />
              </p>
              {selectedCultivar ? (
                <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                  <span className="text-sm font-medium text-green-800">
                    {selectedCultivar.cultivar_name || selectedCultivar.name}
                    {selectedCultivar.trade_name && ` (${selectedCultivar.trade_name})`}
                  </span>
                  <button
                    type="button"
                    onClick={() => { setSelectedCultivar(null); setValue('cultivar_id', 0); }}
                    className="text-green-600 hover:text-green-800 ml-auto"
                  >
                    {t('change')}
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
                    <input
                      type="text"
                      value={cultivarQuery}
                      onChange={e => setCultivarQuery(e.target.value)}
                      placeholder={t('cultivarSearch')}
                      className="w-full border border-neutral-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  {cultivarResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {cultivarResults.map((c: any) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => handleSelectCultivar(c)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-neutral-50 border-b border-neutral-100 last:border-b-0"
                        >
                          <span className="font-medium">{c.cultivar_name || c.name}</span>
                          {c.trade_name && <span className="text-neutral-400 ml-1">({c.trade_name})</span>}
                        </button>
                      ))}
                    </div>
                  )}
                  {!licensedLoaded && (
                    <p className="text-xs text-neutral-400 mt-1">{t('loadingCultivars')}</p>
                  )}
                  {licensedLoaded && licensedCultivars.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">
                      {t('noLicences')}
                    </p>
                  )}
                  {licensedLoaded && licensedCultivars.length > 0 && cultivarQuery.length >= 1 && cultivarResults.length === 0 && (
                    <p className="text-xs text-neutral-400 mt-1">{t('noCultivarMatch')}</p>
                  )}
                </div>
              )}
              {errors.cultivar_id && (
                <p className="text-xs text-red-500 mt-1">{errors.cultivar_id.message}</p>
              )}
            </div>

            {/* Rootstock search (optional) */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">{t('rootstock')}</label>
              {selectedRootstock ? (
                <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                  <span className="text-sm font-medium text-blue-800">
                    {selectedRootstock.cultivar_name || selectedRootstock.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => { setSelectedRootstock(null); setValue('rootstock_id', undefined); }}
                    className="text-blue-600 hover:text-blue-800 ml-auto"
                  >
                    {t('remove')}
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
                    <input
                      type="text"
                      value={rootstockQuery}
                      onChange={e => setRootstockQuery(e.target.value)}
                      placeholder={t('rootstockSearch')}
                      className="w-full border border-neutral-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  {rootstockResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {rootstockResults.map((r: any) => (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => handleSelectRootstock(r)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-neutral-50 border-b border-neutral-100 last:border-b-0"
                        >
                          <span className="font-medium">{r.cultivar_name || r.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {rootstockQuery && rootstockResults.length === 0 && licensedLoaded && (
                    <p className="text-xs text-neutral-400 mt-1">
                      {t('noRootstockMatch')}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Tree type */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                {t('treeType')} <span className="text-red-500">*</span>
              </label>
              <select
                {...register('tree_type_id', { setValueAs: optionalNumber })}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">{t('selectTreeType')}</option>
                {treeTypes.filter(tt => tt.is_active).map(tt => (
                  <option key={tt.id} value={tt.id}>
                    {t('treeTypeOption', { name: tt.name, code: tt.code, stockType: tt.stock_type === 'B' ? t('bareroot') : t('container') })}
                  </option>
                ))}
              </select>
              {errors.tree_type_id && (
                <p className="text-xs text-red-500 mt-1">{errors.tree_type_id.message}</p>
              )}
            </div>
            </>)}

            {/* The shop's own grouping, for reports and the counter. */}
            <div>
              <label htmlFor="stock-item-category" className="block text-sm font-medium text-neutral-700 mb-1">{t('shopCategory')}</label>
              <select
                id="stock-item-category"
                {...register('category_id', { setValueAs: optionalNumber })}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">{t('none')}</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              {categories.length === 0 && (
                <p className="text-xs text-neutral-500 mt-1">{t('categoriesHelp')}</p>
              )}
            </div>

            {/* SKU */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">{t('skuCode')}</label>
              <input
                type="text"
                {...register('sku_code')}
                placeholder={t('skuPlaceholder')}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            {/* Barcode */}
            <div>
              <label htmlFor="stock-item-barcode" className="block text-sm font-medium text-neutral-700 mb-1">{t('barcode')}</label>
              <input
                id="stock-item-barcode"
                type="text"
                inputMode="numeric"
                {...register('barcode')}
                placeholder={t('barcodePlaceholder')}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              {errors.barcode
                ? <p className="text-xs text-red-500 mt-1">{errors.barcode.message}</p>
                : (
                  <p className="text-xs text-neutral-500 mt-1">
                    {merch
                      ? t('barcodeHelpMerch')
                      : t('barcodeHelpPlant')}
                  </p>
                )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">{t('description')}</label>
              <textarea
                {...register('description')}
                rows={3}
                placeholder={t('descriptionPlaceholder')}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              />
            </div>

            {/* Website price: the public website sells plants only. */}
            {!merch && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">{t('websitePrice')}</label>
              <p className="text-xs text-neutral-400 mb-1">
                {t('websitePriceHelp')}
              </p>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-neutral-400">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  {...register('website_price', { setValueAs: optionalNumber })}
                  placeholder={t('pricePlaceholder')}
                  className="w-full border border-neutral-300 rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              {errors.website_price && (
                <p className="text-xs text-red-500 mt-1">{errors.website_price.message}</p>
              )}
            </div>
            )}

            {/* Offered for sale. Propagation stock (cells, liners, layers) is
                grown on, and is sold only when ticked here. */}
            <div className="pt-1">
              <label className="inline-flex items-center gap-2 text-sm font-medium text-neutral-700">
                <input
                  type="checkbox"
                  checked={sellableShown}
                  onChange={e => setSellable(e.target.checked)}
                  className="rounded border-neutral-300"
                />
                Offered for sale
              </label>
              <p className="text-xs text-neutral-400 mt-1">
                {sellableShown
                  ? 'On the order sheet, the website (with a website price) and the retailer range.'
                  : 'Kept off the order sheet, the website and the retailer range. It can still be grown, potted up and budded.'}
              </p>
            </div>

            {/* Active — edit only. Inactive SKUs drop out of the catalogue and the
                stock item list without being deleted. */}
            {isEdit && (
              <div className="pt-1">
                <label className="inline-flex items-center gap-2 text-sm font-medium text-neutral-700">
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={e => setActive(e.target.checked)}
                    className="rounded border-neutral-300"
                  />
                  {t('active')}
                </label>
                <p className="text-xs text-neutral-400 mt-1">
                  {t('activeHelp')}
                </p>
              </div>
            )}
          </div>

          {/* Suppliers' tag barcodes, so a scan of a bought-in tag finds this item. Saved on their own. */}
          {isEdit && <StockItemBarcodes stockItemId={Number(id)} />}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-between">
            <Button variant="outline" type="button" onClick={() => navigate('/nursery/stock-items')}>
              <ArrowLeft className="h-4 w-4" />
              {t('cancel')}
            </Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? t('saving') : isEdit ? t('saveChanges') : t('create')}
              {!submitting && <Check className="h-4 w-4" />}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
}

export default StockItemForm;
